import React from 'react';
import { useEffect, useContext, useState, useRef } from "react";
import {
  Skeleton,
  TableHead,
  TableCell,
  TableRow,
  Tooltip,
  IconButton,
  Button,
  TableBody
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MUICard from "../../../Component/UI/MUICard/MUICard";
import MUITable from "@mui/material/Table";
import Table, {
  StyledTableCell,
  StyledTableRow,
} from "../../../Component/UI/Table/Table";
import { DiscoveryContext } from "../discovery-context";
import { extractDomain } from "../../../utils/helpers";
import classes from "./CategoryDimensions.module.css";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import api from "../../../BrandOnBoarding/api";
import useGeneratePreCategoryDimensions from "../useGeneratePreCategoryDimensions";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import { InfoOutlined } from "@mui/icons-material";
import { metricExplainations } from "../../../utils/constant";
import DetailedTableModal from "../../BrandAudit/BrandAuditMetricDetails";
import { formatName } from "../../Dashboard/Component/CoreAttributes/CoreAttributes";
import MUIToolTip from "../../../Component/UI/ActionTooltip/MUIToolTip";
import {
  createActionMetric,
  fetchActionMetrics,
} from "../../../redux/actions/metricActions";
import { toast } from "react-toastify";
import { AuthContext } from "../../../context/auth-context";
import { formatDomainData } from "../../BrandAudit/Sources/sourcesOverview";
import { useDispatch } from "react-redux";
import { formatSourceData, formatOldSourceData } from "../../../utils/metric-sources"
import { llmWeb } from "../../../services/apiService";
import BrandAvatar from "../../../Component/Avatar/Avatar"
import useBaseResponsesModal from "../../../Component/BaseResponseComponent/useBaseResponsesModal";
import BaseResponsesModal from "../../../Component/BaseResponseComponent/BaseResponsesModal";
import { ENTITY_LABELS } from "../../../utils/labelMapping";
import Modal from "../../../Component/Modal/Modal";
import { PaginatedCategoryDimensionsTable } from "../../Dashboard/Component/CategoryDimensions/PaginatedCategoryDimensionsTable";

const PRINT_COLS_PER_PAGE = 4;
function filterHeaderForBrands(headEl, fullBrandOrder = [], subsetBrands = []) {
  if (!headEl || !subsetBrands.length) return headEl;
  const keepBrandIdxs = subsetBrands.map((b) => fullBrandOrder.indexOf(b)).filter((i) => i >= 0);
  const headChildren = React.Children.toArray(headEl.props.children);
  if (!headChildren.length) return headEl;
  const rowEl = headChildren[0];
  const cells = React.Children.toArray(rowEl.props.children);
  const filteredCells = cells.filter((_, idx) => {
    if (idx < 1) return true;
    return keepBrandIdxs.includes(idx - 1);
  });
  const newRow = React.cloneElement(rowEl, {}, filteredCells);
  return React.cloneElement(headEl, {}, newRow);
}



export default function CategoryDimensions({ entity, isPrintingMode = false }) {
  const [showModal, setShowModal] = useState(false);
  const discoveryCtx = useContext(DiscoveryContext);
  const { preDimensions } = useGeneratePreCategoryDimensions();
  const [isActionLoading, setIsActionLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const dispatch = useDispatch();
  const customerId = user?.customer_id;
  const category = discoveryCtx.category;
  const llm = discoveryCtx.llm;
  const market = discoveryCtx.market;
  const type = discoveryCtx.type;
  const showOldSourcesRecord = discoveryCtx.state.sourceValidationStatus === "PENDING";
  const isCompanyUser = user?.user_role?.role?.name === "Company user";
  const iswebSearchLLM = llm?.toLowerCase()?.includes("search")
  const baseResponsesData = discoveryCtx?.category_dimensions?.baseResponses || {};
  const headers = discoveryCtx.competitors.map((item) => item.name);

  // Reporting mode state — mirrors MonitoringCategoryDimension
  const [showReportingModal, setShowReportingModal] = useState(false);

  const {
    infoModalOpen,
    infoModalTitle,
    infoModalContent,
    closeInfoModal,
    handleCategoryDimensionClick,
  } = useBaseResponsesModal({ baseResponses: baseResponsesData }, "category-dimension");
  const TABLE_HEADERS = [
    {
      id: "criteria",
      name: ENTITY_LABELS.purchaseCriteria,
    },
  ];

  const totalBrandColumns = discoveryCtx.competitors.length;

  // Derive isReporting from query params — same logic as MonitoringCategoryDimension
  function isReportingMode() {
    const isReporting =
      new URLSearchParams(window.location.search).get("isReporting") === "true" ||
      new URLSearchParams(window.location.search).get("reporting") === "true";
    return isReporting;
  }

  const saveActions = async (actionStatement, explanation, sources, brand) => {
    setIsActionLoading(true);
    const modifiedObj = {
      title: actionStatement,
      rationale: explanation,
      brand,
      persona: "General Persona",
      geography: market == "General Market" ? "General Location" : market,
      market: "General Market",
      category,
      llm,
      isProduct: type === "product" ? true : false,
      sources,
    };

    const payload = {
      action_json: [modifiedObj],
      is_archived: 0,
      is_active: 1,
    };
    dispatch(createActionMetric(customerId, payload))
      .then(() => {
        dispatch(fetchActionMetrics(customerId));
        setIsActionLoading(false);
        toast.success("Action Added!", {
          autoClose: 700,
        });
      })
      .catch(() => {
        setIsActionLoading(false);
        toast.error("Something went wrong!", {
          autoClose: 700,
        });
      });
  }

  function LoadingSkeletion() {
    return (
      <div className="p-3">
        <Skeleton variant="rectangular" height={30} />
        {Array(10)
          .fill(0)
          .map((_, index) => (
            <Skeleton
              variant="rectangular"
              width={index % 2 === 0 ? "70%" : "97%"}
              key={index}
              className="my-3"
              height={20}
            />
          ))}
      </div>
    );
  }

  function renderHeaders(criterias) {
    const headers = [
      ...TABLE_HEADERS,
      ...discoveryCtx.competitors.map((item) => ({
        id: item.name,
        name: item.name,
      })),
    ];

    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headers.map((header, index) => {
            let ctxCompetitor = null;
            if (index !== 0) {
              ctxCompetitor = discoveryCtx.competitors[index - 1];
            }

            let domain = "";
            if (ctxCompetitor && ctxCompetitor?.domain) {
              domain = extractDomain(ctxCompetitor?.domain);
            }

            return (
              <TableCell key={index} className={index == 0 ? classes.table_head_cell_index : classes.table_head_cell}>
                <div className={classes.table_head_content}>
                  {index !== 0 ? (
                    <>
                      {/* Logo with Info Icon overlay */}
                      <div className={classes.logo_container}>
                        <BrandAvatar
                          domainName={domain}
                          brandName={header.name}
                        />

                        {!isCompanyUser && Object.keys(baseResponsesData)?.length > 0 && (iswebSearchLLM || header.name?.toLowerCase() === entity?.toLowerCase()) && (
                          <div className={classes.info_icon_wrapper}>
                            <Tooltip title="View Base Prompt" arrow placement="top">
                              <IconButton
                                size="small"
                                onClick={() => handleCategoryDimensionClick(header.name, entity, iswebSearchLLM, baseResponsesData)}
                                sx={{
                                  padding: '1px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                  color: 'rgba(0, 0, 0, 0.54)',
                                  border: '1px solid rgba(0,0,0,0.08)',
                                  '&:hover': {
                                    backgroundColor: '#2196F3',
                                    color: '#fff',
                                    border: '1px solid #2196F3',
                                  },
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                                }}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: '13px' }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        )}
                      </div>

                      {/* Brand Name below logo */}
                      <div className={classes.table_head_text}>
                        {header.name}
                      </div>
                    </>
                  ) : (
                    // First column - "Purchase Criteria"
                    <div className={classes.table_head_text_comp}>
                      <div className={classes.table_head_title}>
                        <span>{header.name}</span>
                        {!showModal && (
                            <MUIIconButton onClick={() => setShowModal(true)} id="purches_criteria" data-testid="purchase-criteria-info-btn">
                            <InfoOutlined />
                          </MUIIconButton>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  function renderRows(rowItem, tableIndex) {
    return (
      <StyledTableRow key={tableIndex}>
        <StyledTableCell className={classes.attribute_headings}>
          {rowItem.name}
        </StyledTableCell>
        {discoveryCtx.competitors.forEach((comp, index) => {
          const exists = rowItem.brands.some(item =>
            item.name.trim().toLowerCase() === comp.name.trim().toLowerCase()
          );
          if (!exists) {
            rowItem.brands.splice(index, 0, {
              name: comp.name,
              rating: 0,
              sources: [],
              explanation: ""
            });
          }
        })}
        {rowItem.brands.map((item, index) => {
          let color = "#dc3545";
          let rating = "low";
          if (!item.rating) {
            color = "#000";
          }
          if (item.rating >= 7) {
            rating = "fair";
            color = "#2196F3";
          }
          if (item.rating >= 9) {
            color = "#198754";
            rating = "high";
          }
          const attributeSources = Array.isArray(item?.sources) && item?.sources?.length > 0 ? item?.sources : sourcesCategoryDim;
          const isCustomTooltip =
            item?.name?.toLowerCase() === discoveryCtx.entity?.toLowerCase() &&
            iswebSearchLLM &&
            (showOldSourcesRecord || isValidated);

          const sources = isCustomTooltip
            ? (showOldSourcesRecord
              ? formatOldSourceData(attributeSources, llm)
              : formatSourceData(attributeSources, llm))
            : {};

          const TooltipComponent = isCustomTooltip ? MUIToolTip : Tooltip;

          const tooltipProps = isCustomTooltip
            ? {
              metric: ENTITY_LABELS.purchaseCriteria,
              attribute: rowItem.name,
              metricScore: Number(item.rating).toFixed(0),
              selectedInput: {
                llm,
                market,
                category,
                brand: item.name,
                product: item.name,
              },
              addToActionHandler: saveActions,
              actionText: "Add To Action",
              isActionLoading: isActionLoading,
              title: item.explanation,
              arrow: true,
              sources,
              isBrandOverview: true,
              isDiscovery: true,
              isBrandAudit: true,
              slotProps: {
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -18],
                      },
                    },
                  ],
                },
              }
            }
            : {
              title: item.explanation,
              arrow: true,
              slotProps: {
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -18],
                      },
                    },
                  ],
                },
              },
            };

          return (
            item.rating ? (
              <TooltipComponent {...tooltipProps}>
                <StyledTableCell
                  style={{ color }}
                  className={classes.attribute_score}
                >
                  {Number(item.rating).toFixed(0)}
                </StyledTableCell>
              </TooltipComponent>)
              : (
                <StyledTableCell
                  style={{ color }}
                  className={classes.attribute_score}
                >
                  -
                </StyledTableCell>)

          );
        })}
      </StyledTableRow>
    );
  }

  // Transform Discovery competitors into the shape PaginatedCategoryDimensionsTable expects:
  // { id, name } — same as extractUniqueBrands output in Monitoring
  function getUniqueBrandsForPagination() {
    return discoveryCtx.competitors.map((comp, index) => ({
      id: index + 1,
      name: comp.name,
    }));
  }

  // Transform Discovery criterias.data into the shape PaginatedCategoryDimensionsTable expects.
  // Monitoring data uses { name, scores: [{ name, score, delta, explanation }] }
  // Discovery data uses  { name, brands: [{ name, rating, explanation }] }
  function transformDataForPagination(data) {
    return data.map((item) => ({
      name: item.name,
      scores: item.brands.map((brand) => ({
        name: brand.name,
        score: brand.rating,
        delta: null,
        explanation: brand.explanation,
      })),
    }));
  }

  let loading = true;
  let criterias = {};
  let sourcesCategoryDim = [];
  let isValidated = false;
  let hasError = false;
  const transactionStatus = discoveryCtx.state.transactionStatus

  if (
    discoveryCtx?.category_dimensions &&
    Object.keys(discoveryCtx?.category_dimensions).length > 0) {
    criterias = discoveryCtx?.category_dimensions;
    sourcesCategoryDim = criterias?.sources;
    isValidated = criterias?.isValidated;
    loading = false;
  } else if (transactionStatus === "RETRY") {
    hasError = true;
    loading = false;
  } else if (transactionStatus === "RUNNING") {
    hasError = false;
    loading = true;
  }


  return (
    <MUICard variant="elevated" elevation={0} className={classes.card}>
      {!loading && !hasError && criterias?.data?.length > 0 && (
        <>
          {/* Report View button — shown only in reporting mode, mirrors Monitoring */}
          {isReportingMode() && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowReportingModal(true)}
                style={{ textTransform: "none" }}
              >
                {ENTITY_LABELS.purchaseCriteria} Report View
              </Button>
            </div>
          )}

          {/* Always-visible scroll table — UI unchanged */}
          <div className="discovery-ui-table">
            <div className={classes.table_scroll_wrapper}>
              <div
                style={{
                  minWidth: `calc(200px + ${totalBrandColumns} * 130px)`,
                }}
              >
                <Table
                  elevation={0}
                  data={criterias?.data}
                  renderHeaders={() => renderHeaders(criterias?.data)}
                  render={renderRows}
                />
              </div>
            </div>
          </div>

          {/* Always-hidden print container — revealed only by @media print in DiscoveryV2 pageStyle */}
          <div className="discovery-print-table" style={{ display: "none" }}>
            {Array.from({ length: Math.ceil(headers.length / PRINT_COLS_PER_PAGE) }).map((_, pageNum) => {
              const colStart = pageNum * PRINT_COLS_PER_PAGE;
              const colEnd = Math.min(headers.length, colStart + PRINT_COLS_PER_PAGE);
              const pageBrands = headers.slice(colStart, colEnd);
              const filteredHdr = filterHeaderForBrands(renderHeaders(), headers || [], pageBrands);
              const isLast = colEnd >= headers.length;
              return (
                <div
                  key={pageNum}
                  style={{ marginBottom: "16px" }}
                >
                  <MUITable>
                    {filteredHdr}
                    <TableBody>
                      {(criterias?.data || []).map((rowItem, rIdx) => (
                        <StyledTableRow key={rIdx}>
                          <StyledTableCell className={classes.attribute_headings}>
                            {rowItem.name}
                          </StyledTableCell>
                          {pageBrands.map((brand, idx) => {
                            const s = rowItem.brands?.find((br) => br.name?.toLowerCase() === brand?.toLowerCase());
                            const num = s?.rating;
                            let color = "#dc3545";
                            if (num >= 9) color = "#198754";
                            else if (num >= 7) color = "#2196F3";
                            return (
                              <StyledTableCell key={idx} style={{ color }} className={classes.attribute_score}>
                                {num ? Number(num).toFixed(0) : "-"}
                              </StyledTableCell>
                            );
                          })}
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </MUITable>
                </div>
              );
            })}
          </div>



          <DetailedTableModal
            show={showModal}
            onClose={() => setShowModal(false)}
            headers={discoveryCtx.competitors.map((item) => item.name)}
            data={criterias?.data}
            renderHeaders={renderHeaders}
            formatName={formatName}
            classes={classes}
            pdfHeight={300}
            pdfTitle={`${entity}-${ENTITY_LABELS.purchaseCriteria}`}
          />
          <BaseResponsesModal
            open={infoModalOpen}
            title={infoModalTitle}
            content={infoModalContent}
            onClose={closeInfoModal}
          />

          {/* Reporting Modal — mirrors MonitoringCategoryDimension */}
          <Modal
            handleClose={() => setShowReportingModal(false)}
            open={showReportingModal}
            title={`${ENTITY_LABELS.purchaseCriteria} - Report View (Paginated for PPT)`}
            width={1200}
            sx={{
              maxWidth: "90vw",
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            <div style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%"
            }}>
              <div style={{
                maxHeight: "70vh",
                overflowY: "auto",
                padding: "8px",
                width: "100%"
              }}>
                <PaginatedCategoryDimensionsTable
                  data={transformDataForPagination(criterias?.data)}
                  uniqueBrands={getUniqueBrandsForPagination()}
                  brandDomains={
                    // Build a domain map { brandName: domainUrl } from discoveryCtx.competitors
                    discoveryCtx.competitors.reduce((acc, comp) => {
                      acc[comp.name] = comp.domain || "";
                      return acc;
                    }, {})
                  }
                  styleClasses={classes}
                  formatName={formatName}
                  isChecked={false}
                  maxBrandsPerTable={6}
                />
              </div>
            </div>
          </Modal>
        </>
      )}
      {hasError && (
        <div className={classes.error}>
          <ErrorView
            title={`Error Fetching ${ENTITY_LABELS.purchaseCriteria}`}
            message={"Request Failed"}
          />
        </div>
      )}
      {loading && !hasError && <LoadingSkeletion />}
    </MUICard>
  );
}